/**
 * governance/index.ts
 * Canonical public API for the governance system.
 *
 * ARCHITECTURE v4.0 — Pipeline is the canonical validation system.
 * Use runGovernancePipeline() / runPipeline() for all validation.
 *
 * NOTE: Most consumers import directly from sub-modules (e.g.
 * governance/pipeline/orchestrator, governance/ldg/ldgBuilder).
 * This barrel provides a convenience API for the most common imports.
 * Wildcard re-exports (export *) removed in s35 — explicit named exports only.
 * Missing external-facing exports added in s37 — barrel is now complete public API.
 *
 * @see governance/pipeline/orchestrator.ts — canonical entry point
 * @see governance/pipeline/issueContract.ts — canonical issue type (ValidationIssue)
 */

// ─── Pipeline (canonical validation) ──────────────────────────────────────────
export { runGovernancePipeline, runPipeline, runPipelineBatch } from './pipeline/orchestrator';
export type { PipelineOptions } from './pipeline/orchestrator';
export type { GovernancePipelineResult, PipelineBatchResult, PipelineResult, ValidationIssue, GateVerdict, IssueDomain, IssueKind } from './pipeline/issueContract';

// ─── Policy Engine ────────────────────────────────────────────────────────────
export { getPolicy, initializePolicy, isPlusTier, getBaseTierForPlus, familyRequiresVisual, isAssessmentEligibleType, getSkipStages, refreshStoredConfig } from './pipeline/policyEngine';
export type { GovernancePolicy } from './pipeline/policyTypes';
export { DEFAULT_POLICY } from './pipeline/defaultPolicy';

// ─── Visual Registry Sync ─────────────────────────────────────────────────────
export { checkVisualRegistrySync, checkGovernanceMapStaleness, getRegisteredVisualTypes } from './pipeline/visualRegistrySync';
export type { GovernanceMapStalenessReport } from './pipeline/visualRegistrySync';

// ─── Governance Map Generator (auto-generates MAP from seeds) ─────────────────
export { generateGovernanceMapFromSeeds, getGeneratedGovernanceMap, getGeneratedMapFamilyCount, invalidateGovernanceMapCache } from './pipeline/generateGovernanceMap';
export type { GeneratedVisualGovernanceMap } from './pipeline/generateGovernanceMap';
export { invalidateMapCache } from './pipeline/visualPolicyBuilder';

// ─── LDG (Learning Dependency Graph) ──────────────────────────────────────────
export { buildLDG, calculateFrontier, getAncestors, getDescendants } from './ldg/ldgBuilder';
export { validateDAG } from './ldg/dagValidator';
export { ALL_PREREQUISITES } from './ldg/prerequisites';
export { getLDG } from './ldg';
export type { LDGraph, LDGNode, LDGEdge, FrontierResult, FamilyMasteryInput, FrontierFamily } from './ldg/types';

// ─── Question Quality Engine ──────────────────────────────────────────────────
export { questionQualityEngine, scoreQuestion, buildQualityReport } from './questionQualityEngine';
export type { QuestionQualityScore, QuestionQualityReport, QualityDimension } from './questionQualityEngine';

// ─── Question Type Definitions ────────────────────────────────────────────────
export { QUESTION_TYPE_REGISTRY, getQuestionTypeDefinition, getQuestionTypesByCategory, getQuestionTypesRequiringVisual, validateQuestionType, type QuestionTypeDefinition } from './questionTypeDefinitions';

// ─── Cognitive & Didactic Analysis ────────────────────────────────────────────
export { detectCognitiveLevel, cognitiveLevelLabel, cognitiveLevelColor, computeCognitiveLevelDistribution } from './cognitiveLevel';
export type { CognitiveLevel } from './cognitiveLevel';
export { assessExplanationDepth, assessDistractorDepth } from './didacticDepth';

// ─── System Stats ─────────────────────────────────────────────────────────────
export { computeLiveSystemStats, computeModuleStats, computeCalibrationStats } from './systemStats';
export type { GovernanceSystemStats } from './systemStats';

// ─── Governance Config Store ──────────────────────────────────────────────────
export { loadGovernanceConfig, saveGovernanceConfig, resetGovernanceConfig, getDefaultGovernanceConfig } from './pipeline/governanceConfigStore';
export type { GovernanceConfigOverrides } from './pipeline/governanceConfigStore';

// ─── Shared Utilities ─────────────────────────────────────────────────────────
export type { SeverityLevel } from './utils';
export { SEVERITY_ORDER, isGenericText, GENERIC_EXPLANATION_PATTERNS, GENERIC_DISTRACTOR_PATTERNS } from './utils';
export { analyzeNumberPatterns, detectNumberPatternCollapse } from './utils/numberPatternAnalysis';
export type { NumberPatternAnalysis } from './utils/numberPatternAnalysis';
export { analyzeLinguisticVariation, detectLinguisticTemplateCollapse, getLinguisticVariationSuggestions } from './utils/linguisticVariationAnalysis';
export type { LinguisticVariationAnalysis } from './utils/linguisticVariationAnalysis';

// ─── Pedagogical Contract (PEDAGOGICAL_CONTRACT_SPEC) ─────────────────────────
export { runPedagogyStage } from './pipeline/stages/pedagogyStage';
export { estimateDok, batchEstimateDok, generateDokReport } from './utils/dokAutoTagger';
export type { DokEstimate } from './utils/dokAutoTagger';
export { getMisconception, isKnownMisconception, getMisconceptionsForModule, getAllMisconceptionIds, MISCONCEPTION_REGISTRY } from './data/misconceptions';
export type { MisconceptionDefinition } from './data/misconceptions';